import { MigratableJson } from '../MigratableJson';

/**
 * Coerces a dimension value to a string if it is a number.
 * Numbers are converted to "${n}px" format to match the expected dimension format.
 */
const coerceDimensionToString = (value: unknown): unknown => {
  return `${value}px`;
};

/**
 * Migration v15: Coerce numeric dimension values to strings on Image nodes
 *
 * Some Image nodes have numeric `height` and `width` values (e.g., 300 instead of "300px")
 * due to a builder incident on 2/17/2026. These numeric values fail the @IsString() validation
 * in ImagePropsDto, causing pages to show "Error loading page" in the builder.
 *
 * This migration converts any numeric height/width values to string format ("${n}px")
 * on Image nodes, including tablet and mobile breakpoint overrides.
 */
export default function toV15<T extends MigratableJson>(data: T): T {
  const allNodes = data.getAllNodes();

  for (const { node } of allNodes) {
    const props = node.props;
    if (!props || typeof props !== 'object') {
      continue;
    }

    const nextProps: Record<string, unknown> = { ...props };
    let changed = false;

    // Coerce desktop-level dimensions
    if (typeof nextProps.height === 'number') {
      nextProps.height = coerceDimensionToString(nextProps.height);
      changed = true;
    }
    if (typeof nextProps.width === 'number') {
      nextProps.width = coerceDimensionToString(nextProps.width);
      changed = true;
    }

    // Coerce tablet breakpoint dimensions
    if (nextProps.tablet && typeof nextProps.tablet === 'object') {
      const tablet = nextProps.tablet as Record<string, unknown>;
      if (typeof tablet.height === 'number' || typeof tablet.width === 'number') {
        const nextTablet = { ...tablet };
        if (typeof nextTablet.height === 'number') {
          nextTablet.height = coerceDimensionToString(nextTablet.height);
        }
        if (typeof nextTablet.width === 'number') {
          nextTablet.width = coerceDimensionToString(nextTablet.width);
        }
        nextProps.tablet = nextTablet;
        changed = true;
      }
    }

    // Coerce mobile breakpoint dimensions
    if (nextProps.mobile && typeof nextProps.mobile === 'object') {
      const mobile = nextProps.mobile as Record<string, unknown>;
      if (typeof mobile.height === 'number' || typeof mobile.width === 'number') {
        const nextMobile = { ...mobile };
        if (typeof nextMobile.height === 'number') {
          nextMobile.height = coerceDimensionToString(nextMobile.height);
        }
        if (typeof nextMobile.width === 'number') {
          nextMobile.width = coerceDimensionToString(nextMobile.width);
        }
        nextProps.mobile = nextMobile;
        changed = true;
      }
    }

    if (changed) {
      node.props = nextProps;
    }
  }

  return data;
}
